/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { useNavigate } from 'react-router-dom'

import { View } from '@components/atoms'
import { CookieConsent } from '@utils'
import { mono, monoFont } from '@components/systems/monolith/monolith.tokens'

/**
 * 분석 쿠키 동의 — Monolith(2안), 차단형 모달.
 *
 * 2026-09-10: 하단 배너에서 **화면을 덮는 모달**로 바꿨다. 벤츠 EU(mercedes-benz.com,
 * Usercentrics)가 쓰는 형태를 그대로 따랐다 — 스크림 + 스크롤 잠금 + `aria-modal` +
 * **무게가 같은 두 버튼**. 벤츠 코리아(mercedes-benz.co.kr)는 1단계에 "전체 동의"만 두고
 * 거절을 설정 안으로 넣지만, 그 형태는 EU 방문자에게 쓸 수 없어 따르지 않는다
 * (SITEMAP_PLAN §19). judahs 는 전 세계로 판다.
 *
 * **두 버튼은 반드시 같은 규격이어야 한다.** 거절이 수락보다 눈에 덜 띄면 "자유롭게 준 동의"가
 * 아니게 된다 — 이 대칭이 이 컴포넌트의 존재 이유이므로, 한쪽만 블랙 CTA 로 바꾸지 않는다.
 * 그래서 이 화면에는 블랙 CTA 가 없다(DESIGN_SYSTEM §5 의 "페이지당 주 CTA 하나"와 충돌하지
 * 않는 유일한 해법이다 — 둘 다 고스트).
 *
 * 동의 저장·구독은 기존 `CookieConsent` 유틸 그대로다. 선택 전까지 GA·GTM 은 올라오지 않는다.
 * 2026-11-04: 광고(GTM) 동의를 분석과 **한 선택으로 묶었다**(ANALYTICS §11.3 A안, 사용자 결정). 포럼 `jd-analytics.js` 와 문구를 같이 바꾼다.
 * 푸터의 Cookie Settings 가 `reset()` 을 부르면 이 모달이 다시 뜬다.
 * 구 하단 배너는 `CookieConsentBanner.legacy.tsx` 에 보관.
 *
 * 주의: 루트의 `role="dialog"` + `aria-label="Cookie consent"` 선택자를 프리렌더 스크립트가
 * 잡아서 걷어낸다(`scripts/prerender.js`). 두 속성을 바꾸면 그 스크립트도 같이 고쳐야 한다.
 */

/**
 * `<body>` 스크롤 잠금에 쓰는 클래스. 규칙은 `index.css` 에 있고, 위치 보정(`top`)은
 * 이 컴포넌트가 인라인으로 건다. 프리렌더 스크립트가 클래스와 인라인 top 을 함께 지운다.
 */
const BODY_LOCK_CLASS = 'mnCookieLock'

const categoryList: { label: string; body: string }[] = [
	{
		label: 'Essential',
		body: 'Keeps the site working: your cart, your session, and this choice. Always on, because the site cannot run without it.',
	},
	{
		label: 'Analytics & advertising',
		body: 'Google Analytics counts which pages get read. Ad platforms such as Meta and Google Ads, loaded through Google Tag Manager, record which ad brought you here and whether you placed an order, with its total. Those platforms may use this to show you our ads on other sites.',
	},
]

export const CookieConsentBanner = () => {
	// hook
	const navigate = useNavigate()

	// state
	const [isVisible, setIsVisible] = React.useState(() => CookieConsent.get() === null)
	const [isDetailOpen, setIsDetailOpen] = React.useState<boolean>(false)

	const panelRef = React.useRef<HTMLDivElement>(null)
	const lastFocusedRef = React.useRef<Element | null>(null)

	React.useEffect(() => {
		const unsubscribe = CookieConsent.subscribe(value => {
			setIsVisible(value === null)
		})

		return unsubscribe
	}, [])

	/**
	 * 열려 있는 동안 뒤 페이지를 잠근다. 스크림이 클릭을 막아도 스크롤은 남기 때문에,
	 * 벤츠 EU 처럼 `<body>` 를 같이 잠근다 — 안 그러면 스크롤은 되는데 아무것도 눌리지 않아
	 * 고장으로 읽힌다(벤츠 코리아가 그렇다).
	 *
	 * 벤츠 EU 는 `overflow: hidden` 만 건다. **모바일에서는 그것만으로 부족하다** — iOS Safari 는
	 * body 의 overflow 로 터치 스크롤을 막지 못해서, 손가락으로 밀면 스크림 뒤 페이지가 그대로
	 * 움직인다. 그래서 위치를 고정하고 스크롤 양만큼 끌어올린 뒤(`top: -Ypx`), 닫을 때 원래
	 * 자리로 되돌린다. 데스크톱·모바일 양쪽에서 같게 동작한다.
	 */
	React.useEffect(() => {
		if (!isVisible) return

		const scrollY = window.scrollY

		lastFocusedRef.current = document.activeElement
		document.body.classList.add(BODY_LOCK_CLASS)
		document.body.style.top = `-${scrollY}px`
		panelRef.current?.focus()

		return () => {
			document.body.classList.remove(BODY_LOCK_CLASS)
			document.body.style.top = ''
			// 고정을 풀면 브라우저가 맨 위로 되돌린다 — 있던 자리로 다시 놓는다.
			window.scrollTo(0, scrollY)

			const last = lastFocusedRef.current
			if (last instanceof HTMLElement) {
				last.focus()
			}
		}
	}, [isVisible])

	/**
	 * 포커스를 모달 안에 가둔다. Escape 는 일부러 아무 것도 하지 않는다 —
	 * 기본값 없이 닫히면 "선택하지 않음"이 곧 동의가 되어 버린다.
	 */
	const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.key !== 'Tab') return

		const panel = panelRef.current
		if (panel === null) return

		const focusable = panel.querySelectorAll<HTMLElement>(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		)
		if (focusable.length === 0) return

		const first = focusable[0]
		const last = focusable[focusable.length - 1]
		const active = document.activeElement

		if (e.shiftKey && (active === first || active === panel)) {
			e.preventDefault()
			last.focus()
		} else if (!e.shiftKey && active === last) {
			e.preventDefault()
			first.focus()
		}
	}

	if (!isVisible) return null

	return (
		<View
			css={style}
			role="dialog"
			aria-modal="true"
			aria-label="Cookie consent"
			aria-describedby="mnCookieBody"
			onKeyDown={onKeyDown}
		>
			<View className="mnCookiePanel" ref={panelRef} tabIndex={-1}>
				<h2 className="mnCookieTitle">This site uses two kinds of cookies.</h2>

				<p className="mnCookieText" id="mnCookieBody">
					Essential cookies keep the site running. Analytics and advertising cookies tell
					us which pages people use and which ads brought them here. You can change this
					choice at any time from the footer.
				</p>

				<button
					type="button"
					className="mnCookieDetailToggle"
					aria-expanded={isDetailOpen}
					onClick={() => setIsDetailOpen(!isDetailOpen)}
				>
					{isDetailOpen ? 'Hide details' : 'What these do'}
				</button>

				{isDetailOpen && (
					<View className="mnCookieDetail">
						{categoryList.map(aCategory => (
							<View key={aCategory.label} className="mnCookieCategory">
								<span className="mnCookieCategoryLabel">{aCategory.label}</span>
								<p className="mnCookieCategoryBody">{aCategory.body}</p>
							</View>
						))}
					</View>
				)}

				<View className="mnCookieAction">
					<button
						type="button"
						className="mnCookieChoice"
						onClick={() => CookieConsent.set('denied')}
					>
						Essential only
					</button>
					<button
						type="button"
						className="mnCookieChoice"
						onClick={() => CookieConsent.set('granted')}
					>
						Accept all
					</button>
				</View>

				<button
					type="button"
					className="mnCookiePolicy"
					onClick={() => {
						CookieConsent.set('denied')
						navigate('/privacy-policy')
					}}
				>
					Privacy Policy
				</button>
			</View>
		</View>
	)
}

const style = css({
	position: 'fixed',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	// MUI Dialog(1300) 위. 첫 방문에 이보다 위에 떠야 할 것은 없다.
	zIndex: 1400,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: 20,
	// 스크림 — 벤츠 EU 와 같은 70%. 잉크 색을 쓴다(순검정 금지, DESIGN_SYSTEM §2)
	background: 'rgba(22, 24, 27, 0.7)',
	fontFamily: monoFont.sans,
	boxSizing: 'border-box',

	'.mnCookiePanel': {
		width: '100%',
		maxWidth: 640,
		maxHeight: 'calc(100vh - 40px)',
		overflowY: 'auto',
		padding: 'clamp(32px, 4vw, 56px)',
		background: mono.surface,
		border: `1px solid ${mono.line}`,
		borderRadius: 0,
		boxSizing: 'border-box',
		textAlign: 'left',
		outline: 'none',

		'&:focus-visible': {
			outline: `1px solid ${mono.ink}`,
			outlineOffset: 3,
		},
	},

	'.mnCookieTitle': {
		margin: 0,
		fontSize: 'clamp(24px, 2.4vw, 30px)',
		lineHeight: 1.25,
		fontWeight: 200,
		letterSpacing: '-0.01em',
		color: mono.ink,
	},

	'.mnCookieText': {
		margin: '22px 0 0',
		fontSize: 16,
		lineHeight: 1.7,
		fontWeight: 300,
		color: mono.dim,
	},

	'.mnCookieDetailToggle': {
		display: 'inline-block',
		marginTop: 24,
		padding: 0,
		border: 0,
		background: 'transparent',
		fontFamily: monoFont.sans,
		fontSize: 13,
		fontWeight: 500,
		color: mono.dim,
		borderBottom: `1px solid ${mono.lineSoft}`,
		paddingBottom: 2,
		borderRadius: 0,
		cursor: 'pointer',
		transition: 'color 200ms ease-out, border-color 200ms ease-out',

		'&:hover': {
			color: mono.ink,
			borderBottomColor: mono.ink,
		},
	},

	'.mnCookieDetail': {
		marginTop: 24,
		borderTop: `1px solid ${mono.line}`,

		'.mnCookieCategory': {
			display: 'grid',
			gridTemplateColumns: 'minmax(0, 120px) minmax(0, 1fr)',
			gap: 24,
			padding: '18px 0',
			borderBottom: `1px solid ${mono.line}`,

			'.mnCookieCategoryLabel': {
				fontSize: 13,
				letterSpacing: '.14em',
				textTransform: 'uppercase',
				fontWeight: 500,
				color: mono.faint,
			},
			'.mnCookieCategoryBody': {
				margin: 0,
				fontSize: 15,
				lineHeight: 1.7,
				fontWeight: 300,
				color: mono.dim,
			},

			'@media (max-width: 600px)': {
				gridTemplateColumns: '1fr',
				gap: 8,
			},
		},
	},

	/**
	 * 두 버튼은 **같은 규격**이다 — 너비·높이·보더·색이 모두 같다.
	 * 한쪽을 블랙 CTA 로 올리면 거절이 덜 눈에 띄게 되어 이 모달의 전제가 무너진다.
	 */
	'.mnCookieAction': {
		display: 'flex',
		gap: 12,
		marginTop: 36,

		'.mnCookieChoice': {
			flex: '1 1 0',
			display: 'inline-flex',
			alignItems: 'center',
			justifyContent: 'center',
			height: 50,
			padding: '0 20px',
			background: 'transparent',
			color: mono.ink,
			fontFamily: monoFont.sans,
			fontSize: 14,
			fontWeight: 500,
			border: `1px solid ${mono.ink}`,
			borderRadius: 2,
			cursor: 'pointer',
			whiteSpace: 'nowrap',
			transition: 'background-color 200ms ease-out, color 200ms ease-out',

			'&:hover': {
				background: mono.ctaBg,
				color: '#FFFFFF',
			},
		},

		'@media (max-width: 460px)': {
			flexDirection: 'column',

			'.mnCookieChoice': {
				// 세로로 쌓을 때 `flex: 1 1 0` 을 그대로 두면 높이를 나눠 가져 50 이 무너진다.
				flex: '0 0 auto',
				width: '100%',
			},
		},
	},

	'.mnCookiePolicy': {
		display: 'inline-block',
		marginTop: 24,
		padding: 0,
		border: 0,
		background: 'transparent',
		fontFamily: monoFont.sans,
		fontSize: 13,
		fontWeight: 500,
		color: mono.faint,
		borderBottom: '1px solid transparent',
		paddingBottom: 2,
		borderRadius: 0,
		cursor: 'pointer',
		transition: 'color 200ms ease-out',

		'&:hover': {
			color: mono.ink,
			borderBottomColor: mono.ink,
		},
	},

	'@media (prefers-reduced-motion: reduce)': {
		'.mnCookieChoice, .mnCookieDetailToggle, .mnCookiePolicy': { transition: 'none' },
	},
})
